import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { normalizeProfileCode } from '../layouts/main-layout/components/sidebar/config/access-control.config';
import { CompaniesService } from './api/general-settings/companies.service';

export interface BreadcrumbItem {
  label: string;
  routerLink?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class MainLayoutService {
  private userSubject = new BehaviorSubject<any | null>(null);
  user$ = this.userSubject.asObservable();
  private activeProfileSubject = new BehaviorSubject<string | null>(null);
  activeProfile$ = this.activeProfileSubject.asObservable();
  private relatedCodeItemSubject = new BehaviorSubject<string | null>(null);
  relatedCodeItem$ = this.relatedCodeItemSubject.asObservable();
  private relatedClientNameSubject = new BehaviorSubject<string | null>(null);
  relatedClientName$ = this.relatedClientNameSubject.asObservable();
  private relatedBrandNameSubject = new BehaviorSubject<string | null>(null);
  relatedBrandName$ = this.relatedBrandNameSubject.asObservable();
  private sectionSubject = new BehaviorSubject<string | null>(null);
  section$ = this.sectionSubject.asObservable();
  private subsectionSubject = new BehaviorSubject<string | null>(null);
  subsection$ = this.subsectionSubject.asObservable();
  private isManagementPageSubject = new BehaviorSubject<boolean>(false);
  isManagementPage$ = this.isManagementPageSubject.asObservable();
  private managementActionSubject = new BehaviorSubject<string | null>(null);
  managementAction$ = this.managementActionSubject.asObservable();
  private isScopeChangePageSubject = new BehaviorSubject<boolean>(false);
  isScopeChangePage$ = this.isScopeChangePageSubject.asObservable();
  private clientCompanyNameSubject = new BehaviorSubject<string | null>(null);
  clientCompanyName$ = this.clientCompanyNameSubject.asObservable();
  private breadcrumbsSubject = new BehaviorSubject<BreadcrumbItem[] | null>(null);
  breadcrumbs$ = this.breadcrumbsSubject.asObservable();
  private logoUrlSubject = new BehaviorSubject<string>('/assets/logo/logo_gateway.png');
  logoUrl$ = this.logoUrlSubject.asObservable();
  private companySubject = new BehaviorSubject<any | null>(null);
  company$ = this.companySubject.asObservable();
  private impersonationSubject = new BehaviorSubject<{ userId: string; firstName?: string; lastName?: string; auth: any } | null>(null);
  impersonation$ = this.impersonationSubject.asObservable();

  constructor(private companiesService: CompaniesService) {
    this.initializeUserFromLocalStorage();
    this.initializeImpersonationFromLocalStorage();
  }

  setRelatedCodeItem(value: string | null): void {
    this.relatedCodeItemSubject.next(value);
  }

  setRelatedClientName(value: string | null): void {
    this.relatedClientNameSubject.next(value);
  }

  setRelatedBrandName(value: string | null): void {
    this.relatedBrandNameSubject.next(value);
  }

  setSection(section: string | null): void {
    this.sectionSubject.next(section);
  }

  setSubsection(subsection: string | null): void {
    this.subsectionSubject.next(subsection);
  }

  setUser(user: any | null): void {
    this.userSubject.next(user);
  }

  setActiveProfile(profileCode: string | null): void {
    const normalized = normalizeProfileCode(profileCode);
    this.activeProfileSubject.next(normalized ?? profileCode);
  }
  
  setIsManagementPage(isManagement: boolean): void {
    this.isManagementPageSubject.next(isManagement);
  }
  
  setManagementAction(action: string | null): void {
    this.managementActionSubject.next(action);
  }
  
  setIsScopeChangePage(isScopeChangePage: boolean): void {
    this.isScopeChangePageSubject.next(isScopeChangePage);
  }
  
  setClientCompanyName(clientCompanyName: string | null): void {
    this.clientCompanyNameSubject.next(clientCompanyName);
  }

  setBreadcrumbs(breadcrumbs: BreadcrumbItem[] | null): void {
    this.breadcrumbsSubject.next(breadcrumbs);
  }

  setLogoUrl(url: string): void {
    this.logoUrlSubject.next(url);
  }

  getLogoUrl(): string {
    return this.logoUrlSubject.getValue();
  }

  setCompany(company: any | null): void {
    this.companySubject.next(company);
  }

  refreshCompany(): void {
    this.companiesService.me().subscribe({
      next: (response: any) => {
        const company = response?.data?.company;
        if (company) {
          this.setCompany(company);
          const logoUrl = company?.logo?.url;
          if (logoUrl) {
            this.companiesService.getLogo(logoUrl).subscribe({
              next: (publicUrl) => {
                if (publicUrl) this.setLogoUrl(publicUrl);
              },
              error: () => { }
            });
          }
        }
      },
      error: () => { }
    });
  }

  initializeUserFromLocalStorage(): void {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        this.userSubject.next(user);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
  }

  isImpersonating(): boolean {
    return this.impersonationSubject.getValue() !== null;
  }

  getImpersonation(): { userId: string; firstName?: string; lastName?: string; auth: any } | null {
    return this.impersonationSubject.getValue();
  }

  startImpersonation(originalUser: any, originalAuth: any): void {
    const impersonation = {
      userId: originalUser.userId,
      firstName: originalUser.firstName,
      lastName: originalUser.lastName,
      auth: originalAuth
    };
    this.impersonationSubject.next(impersonation);
    localStorage.setItem('impersonation', JSON.stringify(impersonation));
  }

  endImpersonation(): void {
    this.impersonationSubject.next(null);
    localStorage.removeItem('impersonation');
  }

  initializeImpersonationFromLocalStorage(): void {
    const impersonationData = localStorage.getItem('impersonation');
    if (impersonationData) {
      try {
        this.impersonationSubject.next(JSON.parse(impersonationData));
      } catch (error) {
        console.error('Error parsing impersonation data from localStorage:', error);
      }
    }
  }
}
